// Excel import using SheetJS. Supports two modes:
//
//   parseLeadsWorkbook(file)                     — generic mode (B1S 2026 Pipeline.xlsx)
//   parseLeadsWorkbook(file, { source: 'apollo' }) — Apollo export
//
// Apollo mode applies these extra rules:
//   • "Notes" column is treated as the contact's job title (Apollo's quirk)
//     and lands in `contactTitle` instead of `notes`.
//   • Stage "Unqualified" is auto-mapped to "Prospect" so the lead lands in
//     the existing first kanban column.
//   • Lead Source defaults to "Apollo" when missing.
//   • Phones with a leading apostrophe (Excel-text trick) get the apostrophe
//     stripped.
//   • Dates in DD.MM.YYYY format are parsed correctly.
//
// In both modes, blank rows are skipped and unknown enum values produce
// warnings rather than failures.

import * as XLSX from 'xlsx';
import { STAGES, PRODUCTS, SOURCES } from './stages';

const COLUMN_MAP = {
  'lead id':                          'legacyId',
  'company name':                     'companyName',
  'industry/vertical':                'industry',
  'industry':                         'industry',
  'contact name':                     'contactName',
  'contact title':                    'contactTitle',
  'title':                            'contactTitle',
  'job title':                        'contactTitle',
  'phone':                            'phone',
  'email':                            'email',
  'pipeline stage':                   'stage',
  'stage':                            'stage',
  'product interest':                 'productInterest',
  'lead source':                      'leadSource',
  'date added':                       'dateAdded',
  'last activity date':               'lastActivityAt',
  'next action':                      'nextAction',
  'next action date':                 'nextActionDate',
  'owner':                            'ownerName',
  'notes':                            'notes',
  'estimated monthly value (kes)':    'estimatedMonthlyValueKES',
  'estimated monthly value':          'estimatedMonthlyValueKES'
};

// Stage aliases — raw value (lowercased) → canonical stage from STAGES
const STAGE_ALIASES = {
  'unqualified': 'Prospect',
  'new':         'Prospect',
  'cold':        'Prospect',
  'lost':        'Closed Lost',
  'won':         'Closed Won'
};

const SOURCE_ALIASES = {
  'apollo.io':      'Apollo',
  'linkedin':       'LinkedIn Personal',
  'lusha':          'Lusha Email',
  'cold email':     'Apollo'
};

export async function parseLeadsWorkbook(file, opts = {}) {
  const isApollo = opts.source === 'apollo';
  const buf = await file.arrayBuffer();
  const wb  = XLSX.read(buf, { type: 'array', cellDates: true });

  // Pick "Pipeline" sheet if present, else "LeadsUpload" if present, else the first sheet.
  const preferred = ['Pipeline', 'LeadsUpload', 'Leads'];
  const sheetName = preferred.find(n => wb.SheetNames.includes(n)) || wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: null, raw: false });

  const issues = [];
  const leads = [];
  const fileColumns = rows[0] ? Object.keys(rows[0]) : [];
  const knownCols = new Set();
  const unknownCols = new Set();
  for (const col of fileColumns) {
    if (COLUMN_MAP[String(col).trim().toLowerCase()]) knownCols.add(col);
    else unknownCols.add(col);
  }

  rows.forEach((row, idx) => {
    const lead = {};
    let hasAny = false;
    for (const [rawKey, value] of Object.entries(row)) {
      const key = COLUMN_MAP[String(rawKey).trim().toLowerCase()];
      if (!key) continue;
      lead[key] = value;
      if (value !== null && value !== '') hasAny = true;
    }
    if (!hasAny) return; // skip blank rows

    // ---- Apollo-specific re-mapping ----
    if (isApollo) {
      // Apollo's "Notes" column actually holds the contact's job title.
      if (lead.notes && !lead.contactTitle) {
        lead.contactTitle = lead.notes;
        lead.notes = '';
      }
      if (!lead.leadSource) lead.leadSource = 'Apollo';
    }

    // ---- Phone cleanup (strip Excel's leading apostrophe) ----
    if (lead.phone) {
      lead.phone = String(lead.phone).replace(/^'+/, '').trim();
    }

    // ---- Lead ID coercion ----
    if (lead.legacyId != null) lead.legacyId = String(lead.legacyId).trim();

    // ---- Numeric coercion ----
    if (lead.estimatedMonthlyValueKES != null) {
      const n = Number(String(lead.estimatedMonthlyValueKES).replace(/[, ]/g, ''));
      lead.estimatedMonthlyValueKES = isNaN(n) ? 0 : n;
    }

    // ---- Date parsing (handles JS Date, ISO, DD.MM.YYYY, DD/MM/YYYY) ----
    if (lead.dateAdded)      lead.dateAdded      = toDate(lead.dateAdded);
    if (lead.nextActionDate) lead.nextActionDate = toDate(lead.nextActionDate);
    if (lead.lastActivityAt) lead.lastActivityAt = toDate(lead.lastActivityAt);

    // ---- Stage normalisation ----
    if (lead.stage) {
      const raw = String(lead.stage).trim();
      const aliased = STAGE_ALIASES[raw.toLowerCase()];
      if (aliased) {
        if (raw.toLowerCase() !== aliased.toLowerCase()) {
          issues.push(`Row ${idx + 2}: stage "${raw}" mapped to "${aliased}".`);
        }
        lead.stage = aliased;
      } else if (!STAGES.includes(raw)) {
        issues.push(`Row ${idx + 2}: unknown stage "${raw}" — defaulted to Prospect.`);
        lead.stage = 'Prospect';
      } else {
        lead.stage = raw;
      }
    }

    // ---- Product normalisation ----
    if (lead.productInterest) {
      const raw = String(lead.productInterest).trim();
      if (!PRODUCTS.includes(raw)) {
        issues.push(`Row ${idx + 2}: unknown product "${raw}" — defaulted to Other.`);
        lead.productInterest = 'Other';
      } else {
        lead.productInterest = raw;
      }
    }

    // ---- Source normalisation ----
    if (lead.leadSource) {
      const raw = String(lead.leadSource).trim();
      const aliased = SOURCE_ALIASES[raw.toLowerCase()];
      if (aliased) {
        lead.leadSource = aliased;
      } else if (!SOURCES.includes(raw)) {
        issues.push(`Row ${idx + 2}: unknown source "${raw}" — defaulted to Website.`);
        lead.leadSource = 'Website';
      } else {
        lead.leadSource = raw;
      }
    }

    // ---- Owner: drop placeholder labels; the importing user becomes owner ----
    if (lead.ownerName) {
      const raw = String(lead.ownerName).trim();
      if (/sales\s*cons/i.test(raw) || raw === '' || raw.toLowerCase() === 'unassigned') {
        delete lead.ownerName;       // bulkCreateLeads will fall back to the importing user
      }
    }

    if (!lead.companyName) {
      issues.push(`Row ${idx + 2}: missing company name — skipped.`);
      return;
    }

    leads.push(lead);
  });

  return {
    leads,
    issues,
    sheetName,
    columns: fileColumns,
    knownColumns: [...knownCols],
    unknownColumns: [...unknownCols]
  };
}

// ---- Date helpers ---------------------------------------------------------

const DD_MM_YYYY = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/;
const ISO        = /^(\d{4})-(\d{1,2})-(\d{1,2})/;

function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;

  const s = String(v).trim();

  // ISO first — unambiguous.
  if (ISO.test(s)) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  // DD.MM.YYYY (Apollo / Kenya / EU style)
  const m = s.match(DD_MM_YYYY);
  if (m) {
    const dd = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const yy = parseInt(m[3], 10);
    if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      const d = new Date(yy, mm - 1, dd);
      return isNaN(d.getTime()) ? null : d;
    }
  }

  // Fallback to JS Date — last resort, may be ambiguous.
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}
